import { useEffect, type RefObject } from 'react';

/**
 * Drifting-label physics for the coverage cells.
 *
 * Each locality label is a rectangle with a constant-speed velocity. Labels
 * bounce off the cell walls, off each other, and off the city name sitting in
 * the middle — so the names spread over the whole card instead of banding, and
 * nothing ever settles into a line.
 *
 * Design constraints this is shaped by:
 * - **SSG parity.** Base positions are CSS percentages rendered server-side; the
 *   loop only ever writes a *delta* transform on top. No layout depends on JS
 *   having run, so the prerendered HTML and the hydrated DOM agree.
 * - **No React state.** Positions are written straight to `style.translate` at
 *   60fps. Routing this through state would re-render six subtrees per frame.
 * - **Idle when unseen.** An IntersectionObserver parks the loop when the
 *   section is off-screen, and `dt` is clamped so a backgrounded tab doesn't
 *   teleport every label on return.
 */

type Body = {
  el: HTMLElement;
  /** Base position in px, from the server-rendered percentage layout. */
  bx: number;
  by: number;
  /** Offset from base, which is what we write to the element. */
  dx: number;
  dy: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
};

type Rect = { x: number; y: number; w: number; h: number };

/** Deterministic PRNG — same drift every load, and identical across SSG/hydration. */
function seeded(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function hash(str: string) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

/** px per second — slow enough to read a name while it moves. */
const SPEED_MIN = 5;
const SPEED_MAX = 12;

/** Gap held between labels, and each body's share of resolving an overlap.
 *  Must be 0.5 — an even split. Anything higher overshoots (two bodies each
 *  moving 0.9x the overlap separate by 1.8x it), which throws them into fresh
 *  overlaps and makes the relaxation oscillate instead of converge. */
const SEPARATION_PAD = 3;
const SEPARATION_GAIN = 0.5;

/** Keep-out box (percentages) for the city name in the middle of the cell. */
const RESERVED = { x0: 16, x1: 84, y0: 35, y1: 65 };

/**
 * Deterministic starting scatter, in percentages of the cell.
 *
 * **Stratified, not rejection-sampled.** The cell is divided into a grid and
 * each label takes one slot, jittered inside it. Plain random sampling clumps —
 * and clumping is worse than it looks here, because the anchor cells hide every
 * other label below `sm`, so any bias in the ordering leaves half the card
 * empty. One label per slot means coverage stays even however many are shown.
 *
 * Runs identically on server and client, so the prerendered HTML needs no
 * correction on hydrate, and it doubles as the finished layout for
 * reduced-motion users who never get the physics loop at all.
 */
export function scatterPositions(count: number, seedKey: string, aspect = 1.6) {
  const rand = seeded(hash(seedKey) ^ 0x9e3779b9);

  // Columns come from the cell's aspect, NOT the label count. Deriving them
  // from count gave 8 columns for a 22-label anchor, i.e. a 9%-wide slot — far
  // narrower than "Chikkaballapur". Few columns, many rows: slots stay wider
  // than the labels and the grid grows downward instead.
  const cols = aspect > 1.5 ? 4 : 2;
  const rows = Math.max(2, Math.ceil((count * 1.5) / cols));

  const free: { x: number; y: number }[] = [];
  const vetoed: { x: number; y: number }[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = 12 + ((c + 0.5) / cols) * 76;
      const y = 8 + ((r + 0.5) / rows) * 84;
      const slot = { x, y };
      const inReserved = x > RESERVED.x0 && x < RESERVED.x1 && y > RESERVED.y0 && y < RESERVED.y1;
      (inReserved ? vetoed : free).push(slot);
    }
  }

  // Deterministic Fisher–Yates so slot order — and therefore which labels sit
  // where — is stable across renders.
  for (let i = free.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [free[i], free[j]] = [free[j], free[i]];
  }

  // Vetoed slots are the fallback only if a cell is so crowded that the
  // keep-out box can't be honoured; the solver evicts those on the first frames.
  const slots = free.concat(vetoed);
  const jx = 38 / cols;
  const jy = 42 / rows;

  return Array.from({ length: count }, (_, i) => {
    const slot = slots[i % slots.length];
    return {
      x: slot.x + (rand() - 0.5) * jx,
      y: slot.y + (rand() - 0.5) * jy,
    };
  });
}

function overlaps(a: Rect, b: Rect, pad = 0) {
  return (
    a.x < b.x + b.w + pad && a.x + a.w + pad > b.x && a.y < b.y + b.h + pad && a.y + a.h + pad > b.y
  );
}

export function useFloatingNames(
  containerRef: RefObject<HTMLElement | null>,
  { enabled, seedKey }: { enabled: boolean; seedKey: string },
) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let bodies: Body[] = [];
    let obstacle: Rect | null = null;
    let bounds = { w: 0, h: 0 };
    let frame = 0;
    let last = 0;
    let visible = false;

    const rand = seeded(hash(seedKey));

    const measure = () => {
      const box = container.getBoundingClientRect();
      bounds = { w: box.width, h: box.height };

      const anchor = container.querySelector<HTMLElement>('[data-anchor]');
      if (anchor) {
        const a = anchor.getBoundingClientRect();
        obstacle = { x: a.left - box.left, y: a.top - box.top, w: a.width, h: a.height };
      }

      bodies = Array.from(container.querySelectorAll<HTMLElement>('[data-float]'))
        // A label hidden at this breakpoint measures 0×0 at the origin, which
        // would make it a phantom body colliding with everything top-left.
        .filter((el) => el.getClientRects().length > 0)
        .map((el) => {
          // Read the base from the percentage layout before we write any delta.
          el.style.translate = '';
          const r = el.getBoundingClientRect();
          const angle = rand() * Math.PI * 2;
          const speed = SPEED_MIN + rand() * (SPEED_MAX - SPEED_MIN);
          return {
            el,
            bx: r.left - box.left,
            by: r.top - box.top,
            dx: 0,
            dy: 0,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            w: r.width,
            h: r.height,
          };
        });
    };

    const rectOf = (b: Body): Rect => ({ x: b.bx + b.dx, y: b.by + b.dy, w: b.w, h: b.h });

    /**
     * Wall, obstacle and pairwise constraints. `bounce` is false for the
     * reduced-motion settle pass, which resolves positions without touching
     * velocities — there is no motion for a velocity to describe.
     */
    const solve = (bounce: boolean) => {
      for (const b of bodies) {
        const x = b.bx + b.dx;
        const y = b.by + b.dy;

        // Walls. Reflect and clamp so a label can never leave the cell.
        if (x < 0) {
          b.dx -= x;
          if (bounce) b.vx = Math.abs(b.vx);
        } else if (x + b.w > bounds.w) {
          b.dx -= x + b.w - bounds.w;
          if (bounce) b.vx = -Math.abs(b.vx);
        }
        if (y < 0) {
          b.dy -= y;
          if (bounce) b.vy = Math.abs(b.vy);
        } else if (y + b.h > bounds.h) {
          b.dy -= y + b.h - bounds.h;
          if (bounce) b.vy = -Math.abs(b.vy);
        }

        // The city name is immovable — push the label out along its shallowest axis.
        if (obstacle) {
          const r = rectOf(b);
          if (overlaps(r, obstacle, 4)) {
            const left = r.x + r.w - obstacle.x;
            const right = obstacle.x + obstacle.w - r.x;
            const top = r.y + r.h - obstacle.y;
            const bottom = obstacle.y + obstacle.h - r.y;
            const min = Math.min(left, right, top, bottom);
            if (min === left) {
              b.dx -= left + 4;
              if (bounce) b.vx = -Math.abs(b.vx);
            } else if (min === right) {
              b.dx += right + 4;
              if (bounce) b.vx = Math.abs(b.vx);
            } else if (min === top) {
              b.dy -= top + 4;
              if (bounce) b.vy = -Math.abs(b.vy);
            } else {
              b.dy += bottom + 4;
              if (bounce) b.vy = Math.abs(b.vy);
            }
          }
        }
      }

      // Pairwise separation. n is small per cell (≤22), so O(n²) is ~240
      // comparisons — cheaper than any broadphase structure would be here.
      for (let i = 0; i < bodies.length; i++) {
        for (let j = i + 1; j < bodies.length; j++) {
          const a = bodies[i];
          const b = bodies[j];
          const ra = rectOf(a);
          const rb = rectOf(b);
          if (!overlaps(ra, rb, SEPARATION_PAD)) continue;

          const ox = Math.min(ra.x + ra.w - rb.x, rb.x + rb.w - ra.x);
          const oy = Math.min(ra.y + ra.h - rb.y, rb.y + rb.h - ra.y);

          if (ox < oy) {
            const dir = ra.x + ra.w / 2 < rb.x + rb.w / 2 ? -1 : 1;
            a.dx += dir * (ox + SEPARATION_PAD) * SEPARATION_GAIN;
            b.dx -= dir * (ox + SEPARATION_PAD) * SEPARATION_GAIN;
            if (bounce) {
              const t = a.vx;
              a.vx = b.vx;
              b.vx = t;
            }
          } else {
            const dir = ra.y + ra.h / 2 < rb.y + rb.h / 2 ? -1 : 1;
            a.dy += dir * (oy + SEPARATION_PAD) * SEPARATION_GAIN;
            b.dy -= dir * (oy + SEPARATION_PAD) * SEPARATION_GAIN;
            if (bounce) {
              const t = a.vy;
              a.vy = b.vy;
              b.vy = t;
            }
          }
        }
      }
    };

    const write = () => {
      for (const b of bodies) {
        b.el.style.translate = `${b.dx.toFixed(2)}px ${b.dy.toFixed(2)}px`;
      }
    };

    const step = (now: number) => {
      frame = requestAnimationFrame(step);
      if (!visible) {
        last = now;
        return;
      }
      // Clamped so a throttled/backgrounded tab resumes smoothly instead of
      // integrating one enormous step and flinging every label out of frame.
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (dt <= 0) return;

      for (const b of bodies) {
        b.dx += b.vx * dt;
        b.dy += b.vy * dt;
      }
      solve(true);
      write();
    };

    // Relax the stratified start layout into a non-overlapping one before
    // anything paints. Reduced motion needs this because no loop follows, but
    // everyone benefits: without it the first second of the animation is labels
    // shoving each other apart, which reads as a bug rather than as drift.
    const settle = () => {
      for (let i = 0; i < 120; i++) solve(false);
      write();
    };

    // Fonts change label widths; measuring before they load gives wrong boxes.
    const start = () => {
      measure();
      settle();
      if (!enabled) return;
      last = performance.now();
      frame = requestAnimationFrame(step);
    };
    if (document.fonts?.status === 'loaded') start();
    else void (document.fonts?.ready ?? Promise.resolve()).then(start);

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
      },
      { rootMargin: '100px' },
    );
    io.observe(container);

    const ro = new ResizeObserver(() => {
      for (const b of bodies) {
        b.dx = 0;
        b.dy = 0;
        b.el.style.translate = '';
      }
      measure();
      settle();
    });
    ro.observe(container);

    return () => {
      cancelAnimationFrame(frame);
      io.disconnect();
      ro.disconnect();
      for (const b of bodies) b.el.style.translate = '';
    };
  }, [containerRef, enabled, seedKey]);
}
